import { redirect } from 'react-router'
import type { LoaderFunctionArgs } from 'react-router'

import { isStagingUser, getRole, getUser, fetchCurrentUser } from '../services/auth'
import { getFeatures } from '../services/api/features'
import { accessService } from '../services/access.service'

export async function loader({ request }: LoaderFunctionArgs) {
  // Carry incoming query params (taskId, tenantUuid, c_selections, referrer, …) into
  // whichever role-based destination we redirect to. Without this, a bare
  // `redirect('overview')` strips any deep-link params that survived the SSO handoff.
  const incomingSearch = new URL(request.url).search

  const redirectWithSearch = (path: string) => {
    if (!incomingSearch) return redirect(path)
    return redirect(path.includes('?') ? `${path}&${incomingSearch.slice(1)}` : `${path}${incomingSearch}`)
  }

  // Ensure user data is loaded before checking permissions
  try {
    // Check if user is already in localStorage
    const cachedUser = getUser()
    if (!cachedUser) {
      await fetchCurrentUser()
    }
  } catch (error) {
    console.error('Error fetching user:', error)
    // Continue anyway - maybe user is logged in via cookies
  }

  // If the URL carries a deep-link taskId, route to the task detail modal so the
  // deep-link survives even when the user lands on `/` (e.g. via a stale legacy redirect).
  const incomingParams = new URL(request.url).searchParams
  const deepLinkTaskId = incomingParams.get('taskId')
  if (deepLinkTaskId) {
    const cleaned = new URLSearchParams(incomingParams)
    cleaned.delete('taskId')
    const remaining = cleaned.toString()
    return redirect(`Tasks/detail/${deepLinkTaskId}${remaining ? `?${remaining}` : ''}`)
  }

  // Use direct ES module imports for user type checks
  try {
    const isStaging = isStagingUser()
    const role = getRole()

    if (isStaging || role === 'RCR_USER') {
      return redirectWithSearch('staging/overview')
    }
  } catch (error) {
    console.error('Error checking user type:', error)
  }

  // Replicate legacy checks for etmfFeature and canAccessClinical
  let etmfFeature = false
  let canAccessClinical = false

  try {
    etmfFeature = await getFeatures().then((f) => f.etmfenabled)

    // Check clinical access with timeout to prevent hanging
    // Pass redirectOnFailure: false to prevent access service from redirecting
    // We handle the redirect logic in this loader instead
    const accessPromise = accessService.isAllowedStateAsync('clinicalAccess', {
      hideDontAccessErrorMsg: true,
      redirectOnFailure: false
    })
    const timeoutPromise = new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(false), 1000)
    })

    canAccessClinical = await Promise.race([accessPromise, timeoutPromise])
  } catch (e) {
    // fallback: treat as not enabled
    console.error('Error in getFeatures or isAllowedStateAsync:', e)
  }

  if (etmfFeature && canAccessClinical) {
    return redirectWithSearch('TrialOverview/status')
  }

  // Default fallback - redirect to overview
  // Use relative path (no leading slash) to match the route definition as a child of root
  // The route is defined as: { path: ROUTE_PATHS.OVERVIEW } where OVERVIEW = 'overview'
  return redirectWithSearch('overview')
}

export default function Index() {
  // This shouldn't render since we redirect, but just in case
  return null
}
